/**
 * ============================================================================
 * DURATION RANGE — builds the absolute week tokens for getMetrics.
 * ============================================================================
 *
 * The getMetrics contract takes absolute Canonical Week Tokens
 * ("<year> Week <NN>", zero-padded — e.g. "2026 Week 04"), not relative
 * offsets. The week universe comes from getWeekNumbers (-55..+4 around the
 * current week) already in that format; this module slices a window out of
 * that universe so week arithmetic — including year boundaries — never
 * happens on the client.
 */

/**
 * Normalize any supported week format to the Canonical Week Token getMetrics
 * validates: "2026-WK7" | "2026 Week 7" → "2026 Week 07" (NN zero-padded).
 * Compact tokens ("2026Week30") are REJECTED by the backend — never send them.
 */
export function toCanonicalWeekToken(week: string): string {
  const match = week.match(/^(\d{4})[\s-]*(?:WK|Week)\s*(\d+)$/i);
  if (!match) return week;
  return `${match[1]} Week ${match[2].padStart(2, '0')}`;
}

/** Format any supported week token for display: "2026 Week 30". */
export function toWeekLabel(week: string): string {
  const match = week.match(/^(\d{4})[\s-]*(?:WK|Week)\s*(\d+)$/i);
  if (!match) return week;
  return `${match[1]} Week ${parseInt(match[2], 10)}`;
}

/**
 * The week immediately before `week` in the universe. Used as the snapshot
 * anchor: a snapshot is the frozen WBR capture of COMPLETED weeks, so its
 * window ends at the last completed week, not the running one.
 */
export function weekBefore(weeks: string[], week: string): string {
  const index = weeks.indexOf(week);
  return index > 0 ? weeks[index - 1] : week;
}

/** Week-control defaults — MUST match dashboardSlice initial state. */
export const DEFAULT_LAST_X_WEEKS = 4;
export const DEFAULT_FORWARD_X_WEEKS = 4;

/**
 * True when the week controls are untouched. Windows are always sent
 * explicitly now (weeks are cached individually, so pages share weeks rather
 * than sharing one whole-window entry), so this no longer decides whether to
 * omit the period list — it just reports whether the user has customised the
 * window. Snapshot mode pins forwardXWeeks to 0 (dataModeChanged), so 0 IS the
 * snapshot default.
 */
export function isDefaultWindow(
  reportingWeek: string | null,
  lastX: number,
  forwardX: number,
  isSnapshot: boolean,
): boolean {
  return (
    reportingWeek === null &&
    lastX === DEFAULT_LAST_X_WEEKS &&
    forwardX === (isSnapshot ? 0 : DEFAULT_FORWARD_X_WEEKS)
  );
}

/**
 * The weeks a user may ANCHOR on, newest first — the dropdown options shared
 * by the Home and Customer Domain pages (spec domains-page-week-picker R1.2).
 * Live: every week up to and including the current one. Snapshot: the same
 * minus the running current week (its capture doesn't exist until week
 * close). The universe's +4 forward weeks are never anchor options.
 */
export function availableAnchorWeeks(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  isSnapshot: boolean,
): string[] {
  if (!weeks?.length || !currentWeek) return [];
  const upTo = weeks.indexOf(currentWeek);
  if (upTo === -1) return [];
  const inclusive = isSnapshot ? upTo - 1 : upTo;
  // Canonical tokens: the dropdown shows them and `selectedWeekForPicker`
  // matches its resolved anchor (also canonical) against them, so both must
  // be in the SAME form — returning raw tokens breaks the preselection.
  return weeks
    .slice(0, inclusive + 1)
    .map(toCanonicalWeekToken)
    .reverse();
}

/**
 * The tiles page's window: exactly ONE week (R4.2). Defaults to the running
 * week in live mode / the last COMPLETED week in snapshot (a capture is taken
 * at week close), but honors an explicit `reportingWeek` when the user picked
 * one from the Customer Domain page's dropdown — the same reporting week the
 * Detailed View reads, so the two pages share the anchor (spec
 * domains-page-week-picker R1.3, R2.1). An unknown/absent `reportingWeek`
 * (or one that is not a valid anchor for the mode — see resolveAnchor) falls
 * back to the mode default.
 */
export function tilesWindow(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  isSnapshot: boolean,
  reportingWeek: string | null = null,
): string[] | undefined {
  if (!weeks?.length || !currentWeek) return undefined;
  const anchor = resolveAnchor(weeks, currentWeek, reportingWeek, isSnapshot);
  return weeks.includes(anchor) ? [toCanonicalWeekToken(anchor)] : undefined;
}

/**
 * Resolve the anchor week for a window: the chosen reporting week when it is
 * a VALID anchor for the mode, else the mode default. Validity is mode-aware,
 * not just membership: an anchor can never be newer than the newest
 * REPORTABLE week — the current week in live, the last COMPLETED week in
 * snapshot (captures exist only for closed weeks). The universe contains +4
 * forward weeks, and a live pick of the current week survives a switch to
 * snapshot — both are in the universe but would anchor a window on a week
 * with no data, so they clamp to the mode default instead. This keeps the
 * fetched window and the pickers' displayed fallback in agreement.
 */
export function resolveAnchor(
  weeks: string[],
  currentWeek: string,
  reportingWeek: string | null,
  isSnapshot: boolean,
): string {
  const newestAnchor = isSnapshot
    ? weekBefore(weeks, currentWeek)
    : currentWeek;
  if (!reportingWeek) return newestAnchor;
  // Compare CANONICALLY, return a raw member of `weeks`. reportingWeek may be
  // stored raw ("2026-WK27") or canonical ("2026 Week 27") — the pickers now
  // dispatch canonical option values while getWeekNumbers serves raw — so a
  // direct indexOf would miss. Canonicalizing both sides makes resolution
  // token-form agnostic; returning weeks[index] keeps the downstream
  // indexOf/slice math (buildDurationWindow, resolveForwardX) working.
  const target = toCanonicalWeekToken(reportingWeek);
  const index = weeks.findIndex((w) => toCanonicalWeekToken(w) === target);
  const newestIndex = weeks.indexOf(newestAnchor);
  return index !== -1 && index <= newestIndex ? weeks[index] : newestAnchor;
}

/** Bounds of the deep dive's Time Period offering (spec deep-dive-periodical R4.3). */
export const DEEP_DIVE_PERIOD_LOOKBACK = 8;
export const DEEP_DIVE_PERIOD_LOOKAHEAD = 4;

/**
 * The weeks the deep dive's Time Period control OFFERS: 8 before the current
 * week + the current week + up to 4 ahead in live mode; snapshot clamps to
 * completed weeks (no capture exists for the running/forward weeks).
 * Deliberately NOT the whole −55-week universe — checking a week fetches its
 * data, so the offering is the bound on that load. Canonical tokens, oldest
 * first (the dropdown reads chronologically, matching the legacy control).
 */
export function deepDivePeriodOptions(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  isSnapshot: boolean,
): string[] {
  if (!weeks?.length || !currentWeek) return [];
  const currentIndex = weeks.indexOf(currentWeek);
  if (currentIndex === -1) return [];
  const start = Math.max(0, currentIndex - DEEP_DIVE_PERIOD_LOOKBACK);
  const end = isSnapshot
    ? currentIndex - 1
    : Math.min(weeks.length - 1, currentIndex + DEEP_DIVE_PERIOD_LOOKAHEAD);
  if (end < start) return [];
  return weeks.slice(start, end + 1).map(toCanonicalWeekToken);
}

export interface DurationWindow {
  /** Anchor week (defaults to currentWeek when no reporting week chosen). */
  anchor: string;
  /** Absolute tokens for getMetrics, oldest first. */
  durationRange: string[];
}

/**
 * Slice a window of `lastX` weeks before and `forwardX` weeks after the
 * anchor out of the getWeekNumbers universe.
 *
 * @param weeks       Ordered week list from getWeekNumbers (oldest first).
 * @param currentWeek Current week from getWeekNumbers.
 * @param reportingWeek Chosen anchor; null → currentWeek.
 * @param lastX       Weeks before the anchor.
 * @param forwardX    Weeks after the anchor (0 in snapshot mode).
 * @returns null while the week universe hasn't loaded yet.
 */
export function buildDurationWindow(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  reportingWeek: string | null,
  lastX: number,
  forwardX: number,
): DurationWindow | null {
  if (!weeks?.length || !currentWeek) return null;

  const anchor =
    reportingWeek && weeks.includes(reportingWeek)
      ? reportingWeek
      : currentWeek;
  const anchorIndex = weeks.indexOf(anchor);
  if (anchorIndex === -1) return null;

  const start = Math.max(0, anchorIndex - lastX);
  const end = Math.min(weeks.length - 1, anchorIndex + forwardX);
  return {
    anchor,
    durationRange: weeks.slice(start, end + 1).map(toCanonicalWeekToken),
  };
}

/**
 * How many forward weeks to show for a window (the window sizes are now fixed —
 * the Last/Forward controls were removed; the user only picks the reporting
 * week).
 *
 * - LIVE: always `DEFAULT_FORWARD_X_WEEKS` (forecast columns always shown).
 * - SNAPSHOT: only the weeks that actually sit BETWEEN the reporting week and
 *   the current week — a snapshot can't show weeks that hadn't happened when it
 *   was taken. With current = week 34: anchor 33 → 0 forward, 32 → 1 (week 33),
 *   31 → 2, capped at `DEFAULT_FORWARD_X_WEEKS`. Formula:
 *   `clamp((currentIndex - 1) - anchorIndex, 0, DEFAULT_FORWARD_X_WEEKS)`.
 */
export function resolveForwardX(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  anchor: string,
  isSnapshot: boolean,
): number {
  if (!isSnapshot) return DEFAULT_FORWARD_X_WEEKS;
  if (!weeks?.length || !currentWeek) return 0;
  const currentIndex = weeks.indexOf(currentWeek);
  const anchorIndex = weeks.indexOf(anchor);
  if (currentIndex === -1 || anchorIndex === -1) return 0;
  return Math.max(
    0,
    Math.min(DEFAULT_FORWARD_X_WEEKS, currentIndex - 1 - anchorIndex),
  );
}

/**
 * THE window for the tables (spec reporting-week-only-window): fixed
 * `DEFAULT_LAST_X_WEEKS` before the anchor + mode-derived forward weeks
 * (resolveForwardX). The anchor is the chosen reporting week, defaulting to the
 * current week (live) or the last completed week (snapshot — captures exist
 * only for closed weeks). This is the single source both the tables and the
 * page-level population counter build from.
 */
export function resolveWindow(
  weeks: string[] | undefined,
  currentWeek: string | undefined,
  reportingWeek: string | null,
  isSnapshot: boolean,
): DurationWindow | null {
  if (!weeks?.length || !currentWeek) return null;
  const anchor = resolveAnchor(weeks, currentWeek, reportingWeek, isSnapshot);
  const forwardX = resolveForwardX(weeks, currentWeek, anchor, isSnapshot);
  return buildDurationWindow(
    weeks,
    currentWeek,
    anchor,
    DEFAULT_LAST_X_WEEKS,
    forwardX,
  );
}
